import { useCallback, useEffect, useRef, useState } from "react";
import { MccApi } from "lib/api/mccApiWrapper";
import { PlantUserAccessManagerContactInfo } from "models/dto/user";
import { UsePermissionRefresh } from "./useSectorsAndPermissions";

/**
 * Loads plant user access manager contact info on mount, with loading/error state and permission refresh
 * on recoverable failures (same pattern as sector admin contact hook).
 *
 * @returns `contactInfo` sectors from the API (client-side sort may apply on the page), `loading`, `error`
 */
export function UsePlantUserAccessManagersContactInfo() {
	const [contactInfo, setContactInfo] = useState<
		PlantUserAccessManagerContactInfo[]
	>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const fetchingRef = useRef(false);

	const { handlePermissionError } = UsePermissionRefresh();

	const fetchContactInfo = useCallback(async () => {
		if (fetchingRef.current) {
			return;
		}

		try {
			fetchingRef.current = true;
			setLoading(true);
			setError("");

			const api = MccApi.GetInstance((window as any).store);
			const resp = await api.FindPlantUserAccessManagersContactInfo();
			setContactInfo(resp);
		} catch (e: any) {
			try {
				await handlePermissionError(e);
			} catch (retryError: any) {
				setError(
					retryError?.response?.data?.message ||
						retryError?.message ||
						"Failed to load plant user access managers contact information."
				);
				setContactInfo([]);
			}
		} finally {
			setLoading(false);
			fetchingRef.current = false;
		}
	}, [handlePermissionError]);

	useEffect(() => {
		fetchContactInfo();
	}, [fetchContactInfo]);

	return { contactInfo, loading, error };
}
